import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Lock, CheckCircle2, Clock, AlertTriangle, ArrowRight, Truck,
  ClipboardCheck, Send, Copy, Bell, Calendar, Eye, Sparkles,
  Info, ChevronRight, ShieldCheck, Link2, MessageSquare, Target, Zap,
  XCircle, HardHat, Users
} from "lucide-react";

const stateStyles = {
  cleared: { label: "Cleared", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: CheckCircle2, ring: "ring-emerald-400" },
  locked: { label: "Not Ready", bg: "bg-red-50", border: "border-red-200", text: "text-red-700", icon: Lock, ring: "ring-red-400" },
  waiting: { label: "Waiting", bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-600", icon: Clock, ring: "ring-slate-300" },
};
const condStatusStyles = {
  cleared: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "needs-review": "bg-amber-50 text-amber-700 border-amber-200",
  blocker: "bg-red-50 text-red-700 border-red-200",
  "in-progress": "bg-blue-50 text-blue-700 border-blue-200",
  "not-started": "bg-slate-100 text-slate-500 border-slate-200",
};

function GatePipeline({ stages, activeStage, onSelect }) {
  return (
    <div className="flex items-center gap-0 w-full" data-testid="gate-pipeline">
      {stages.map((s, i) => {
        const st = stateStyles[s.state] || stateStyles.waiting;
        const Icon = st.icon;
        const isActive = s.id === activeStage;
        return (
          <div key={s.id} className="flex items-center flex-1">
            <button
              onClick={() => onSelect(s.id)}
              data-testid={`gate-${s.id}`}
              className={`relative flex-1 py-3 px-3 rounded-xl border-2 text-center transition-all ${isActive ? `${st.border} ${st.bg} shadow-md ring-2 ${st.ring}` : 'border-slate-200 bg-white hover:bg-slate-50'}`}
            >
              <Icon className={`w-5 h-5 mx-auto mb-1 ${isActive ? st.text : 'text-slate-400'}`} />
              <p className={`text-[10px] font-bold uppercase tracking-wider ${isActive ? st.text : 'text-slate-500'}`}>{s.label}</p>
              <p className={`text-[9px] mt-0.5 ${isActive ? st.text : 'text-slate-400'}`}>
                {s.state === 'cleared' ? `${s.conditions.length}/${s.conditions.length}` : `${s.conditions.filter(c => c.status === 'cleared').length}/${s.conditions.length}`}
              </p>
              {s.blockers.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{s.blockers.length}</span>
              )}
            </button>
            {i < stages.length - 1 && (
              <div className="w-6 flex items-center justify-center shrink-0">
                <ChevronRight className={`w-4 h-4 ${stages[i].state === 'cleared' ? 'text-emerald-400' : 'text-slate-300'}`} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function StageReadiness() {
  const { gates, conditions, blockers, trades, activeProject, workspace, analytics, activeLens, lens, resolveBlocker, sendChase, logAction, tradeReadiness } = useDemoState();
  const projectGates = gates.filter(g => g.projectId === activeProject);
  const projectConds = conditions.filter(c => c.projectId === activeProject);
  const projectBlockers = blockers.filter(b => b.projectId === activeProject);

  const stages = useMemo(() => projectGates.map(g => {
    const stageConds = projectConds.filter(c => c.stage === g.stage);
    const stageBlockers = projectBlockers.filter(b => b.stage === g.stage);
    const stageTrades = trades.filter(t => t.projectId === activeProject && t.stage === g.stage);
    return {
      id: g.stage.toLowerCase().replace(/\s+/g, '-'),
      label: g.milestone, stage: g.stage, state: g.state,
      tradeRelease: stageTrades[0]?.status || 'waiting',
      surveyorReady: g.state === 'cleared',
      delayDays: stageBlockers.filter(b => b.severity === 'critical').length > 0 ? Math.max(2, stageBlockers.length * 2) : 0,
      blockers: stageBlockers,
      conditions: stageConds,
      trades: stageTrades,
      totalConditions: g.totalConditions,
      clearedConditions: g.clearedConditions,
    };
  }), [projectGates, projectConds, projectBlockers, trades, activeProject]);

  const [activeStage, setActiveStage] = useState(stages.find(s => s.state === 'locked')?.id || stages[1]?.id || stages[0]?.id);
  const [selectedBlocker, setSelectedBlocker] = useState(null);
  const stage = stages.find(s => s.id === activeStage) || stages[0];
  const style = stage ? (stateStyles[stage.state] || stateStyles.waiting) : stateStyles.waiting;

  const handleResolve = (blockerId) => {
    resolveBlocker(blockerId);
    setSelectedBlocker(null);
    toast.success('Blocker resolved', { description: 'Condition cleared. Stage readiness updated. Trade release status may have changed.' });
  };

  const handleChase = (blockerId) => {
    sendChase(blockerId, 'email');
    toast.success('Chase sent', { description: 'Follow-up email dispatched.' });
  };

  if (!stage) return <AppLayout><div className="p-8 text-center text-slate-400">No stage gates for this project</div></AppLayout>;

  const StIcon = style.icon;

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="stage-readiness-page">
        {/* Gate pipeline */}
        <GatePipeline stages={stages} activeStage={activeStage} onSelect={setActiveStage} />

        {/* Hero card */}
        <Card className={`${style.bg} ${style.border} border-2`} data-testid="readiness-hero">
          <CardContent className="py-5 px-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stage.state === 'cleared' ? 'bg-emerald-100' : stage.state === 'locked' ? 'bg-red-100' : 'bg-slate-100'}`}>
                  <StIcon className={`w-6 h-6 ${style.text}`} />
                </div>
                <div>
                  <h2 className={`text-lg font-heading font-bold ${style.text}`}>{stage.label}</h2>
                  <p className="text-sm text-slate-600 mt-0.5">{style.label} &middot; {stage.conditions.filter(c => c.status === 'cleared').length}/{stage.conditions.length} conditions cleared</p>
                  {stage.blockers.length > 0 && <p className="text-xs text-red-600 mt-1 font-medium">{stage.blockers.length} blocker(s) preventing clearance</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge className={`text-xs py-1 px-3 ${stage.tradeRelease === 'hold' ? 'bg-red-100 text-red-700' : stage.tradeRelease === 'released' ? 'bg-emerald-100 text-emerald-700' : stage.tradeRelease === 'ready' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  <Truck className="w-3 h-3 mr-1" /> Trade: {stage.tradeRelease === 'hold' ? 'Locked' : stage.tradeRelease === 'released' ? 'Released' : stage.tradeRelease === 'ready' ? 'Ready' : 'Waiting'}
                </Badge>
                <Badge className={`text-xs py-1 px-3 ${stage.surveyorReady ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  <ClipboardCheck className="w-3 h-3 mr-1" /> Surveyor: {stage.surveyorReady ? 'Ready' : 'Not ready'}
                </Badge>
                {stage.delayDays > 0 && <Badge className="text-xs py-1 px-3 bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3 mr-1" /> ~{stage.delayDays} day delay risk</Badge>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What changes if cleared */}
        {stage.state === 'locked' && (
          <Card className="border-blue-200 bg-blue-50/20" data-testid="what-changes-if-cleared">
            <CardContent className="py-3.5 px-5">
              <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-blue-600" /><span className="text-xs font-bold text-blue-700">What changes if this stage clears</span></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {stage.tradeRelease === 'hold' && (
                  <div className="flex items-start gap-2 p-2.5 bg-white rounded-lg border border-blue-100">
                    <Truck className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
                    <div><p className="text-[10px] font-semibold text-blue-700">Trade unlocks</p><p className="text-[10px] text-blue-600">{trades.filter(t => t.stage === stage.stage && t.status === 'hold').map(t => t.trade).join(', ') || 'Pending trade'} can be released</p></div>
                  </div>
                )}
                <div className="flex items-start gap-2 p-2.5 bg-white rounded-lg border border-blue-100">
                  <ClipboardCheck className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
                  <div><p className="text-[10px] font-semibold text-blue-700">Surveyor notified</p><p className="text-[10px] text-blue-600">Readiness pack can be sent for inspection</p></div>
                </div>
                <div className="flex items-start gap-2 p-2.5 bg-white rounded-lg border border-blue-100">
                  <Calendar className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
                  <div><p className="text-[10px] font-semibold text-blue-700">Calendar updated</p><p className="text-[10px] text-blue-600">Release window and inspection dates confirmed</p></div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analytics strip */}
        {analytics && stage.blockers.length > 0 && activeLens !== 'client' && activeLens !== 'buyer' && (
          <div className="flex items-center gap-3 flex-wrap" data-testid="readiness-analytics">
            {analytics.topBlockerCause && (
              <div className="flex items-center gap-1.5 bg-red-50 border border-red-100 rounded-lg px-3 py-1.5">
                <AlertTriangle className="w-3 h-3 text-red-500" />
                <span className="text-[10px] font-semibold text-red-700">Top cause: {analytics.topBlockerCause.cause.replace(/-/g, ' ')}</span>
              </div>
            )}
            {analytics.topOverdueOwner && (
              <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5">
                <Clock className="w-3 h-3 text-amber-500" />
                <span className="text-[10px] font-semibold text-amber-700">Most overdue: {analytics.topOverdueOwner.owner}</span>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Blockers */}
            {stage.blockers.length > 0 && (
              <Card className="border-red-200 bg-red-50/30" data-testid="blockers-list">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-heading flex items-center gap-2 text-red-700"><Lock className="w-4 h-4" /> {stage.blockers.length} Blockers</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {stage.blockers.map(b => (
                    <div key={b.id} className="flex items-center justify-between py-3 px-4 bg-white border border-red-100 rounded-lg cursor-pointer hover:shadow-sm transition-all" onClick={() => setSelectedBlocker(b)} data-testid={`blocker-${b.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-red-500" /></div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{b.title}</p>
                          <p className="text-xs text-slate-500">{b.type} &middot; Owner: {b.owner}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <Badge className={`text-[9px] ${b.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{b.severity}</Badge>
                        <span className="text-xs text-slate-400">Due {b.dueDate}</span>
                        <ArrowRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Conditions table */}
            <Card className="border-slate-200" data-testid="conditions-table">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Conditions for {stage.label}</CardTitle></CardHeader>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-t border-slate-100 bg-slate-50/50">
                      <th className="text-left py-2 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Condition</th>
                      <th className="text-left py-2 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Owner</th>
                      <th className="text-left py-2 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Due</th>
                      <th className="text-left py-2 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stage.conditions.map(c => (
                      <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                        <td className="py-2.5 px-4">
                          <p className="font-medium text-slate-900">{c.number} {c.title}</p>
                          {c.plainEnglish && <p className="text-[10px] text-slate-400 mt-0.5 max-w-xs truncate">{c.plainEnglish}</p>}
                        </td>
                        <td className="py-2.5 px-4 text-xs text-slate-600">{c.owner}</td>
                        <td className="py-2.5 px-4 text-xs text-slate-500">{c.dueDate}</td>
                        <td className="py-2.5 px-4">
                          <Badge className={`text-[10px] border ${condStatusStyles[c.status] || condStatusStyles['not-started']}`}>
                            {c.status === 'blocker' && <span className="w-1 h-1 rounded-full bg-red-500 mr-1 animate-pulse" />}
                            {c.status.replace('-', ' ')}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Trade Readiness Checks */}
            {stage.trades.length > 0 && (
              <Card className="border-slate-200" data-testid="trade-readiness-checks">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-heading flex items-center gap-2"><HardHat className="w-4 h-4 text-slate-500" /> Trade Readiness ({stage.trades.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {stage.trades.map(t => {
                    const rc = tradeReadiness.find(r => r.tradeId === t.id);
                    const checks = rc?.checks || [];
                    const passCount = checks.filter(c => c.status === 'pass').length;
                    const failCount = checks.filter(c => c.status === 'fail').length;
                    const overallStatus = rc?.overallStatus || (t.status === 'hold' ? 'blocked' : t.status === 'ready' || t.status === 'checked-in' || t.status === 'signed-off' || t.status === 'released' ? 'ready' : 'pending');
                    return (
                      <div key={t.id} className={`p-3 rounded-lg border ${overallStatus === 'ready' ? 'bg-emerald-50/50 border-emerald-100' : overallStatus === 'partial' ? 'bg-amber-50/50 border-amber-100' : overallStatus === 'blocked' ? 'bg-red-50/50 border-red-100' : 'bg-slate-50 border-slate-100'}`} data-testid={`trade-readiness-${t.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Truck className="w-3.5 h-3.5 text-slate-500" />
                            <span className="text-xs font-bold text-slate-800">{t.trade}</span>
                            <span className="text-[10px] text-slate-500">{t.company}</span>
                          </div>
                          <Badge className={`text-[9px] ${overallStatus === 'ready' ? 'bg-emerald-100 text-emerald-700' : overallStatus === 'partial' ? 'bg-amber-100 text-amber-700' : overallStatus === 'blocked' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                            {overallStatus === 'ready' ? 'Ready' : overallStatus === 'partial' ? 'Partial' : overallStatus === 'blocked' ? 'Blocked' : 'Pending'}
                          </Badge>
                        </div>
                        {checks.length > 0 ? (
                          <div className="space-y-1">
                            {checks.map(c => (
                              <div key={c.id} className="flex items-center gap-2 text-[11px]">
                                {c.status === 'pass' ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" /> : c.status === 'fail' ? <XCircle className="w-3 h-3 text-red-500 shrink-0" /> : c.status === 'warning' ? <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" /> : <Clock className="w-3 h-3 text-slate-400 shrink-0" />}
                                <span className="flex-1 text-slate-700">{c.label}</span>
                                <span className="text-[10px] text-slate-400">{c.detail}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-[10px] text-slate-500">
                            <Users className="w-3 h-3" />
                            <span>Status: {t.status} · {t.blockersRemaining} blocker{t.blockersRemaining !== 1 ? 's' : ''}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right sidebar */}
          <div className="space-y-4">
            {/* AI next actions */}
            <Card className="border-indigo-200 bg-gradient-to-b from-indigo-50/50 to-white" data-testid="ai-actions-panel">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading text-indigo-800 flex items-center gap-2"><Sparkles className="w-4 h-4" /> What to do next</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {stage.blockers.length > 0 ? stage.blockers.map(b => (
                  <div key={b.id} className="bg-white border border-indigo-100 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-medium text-slate-800">{b.title}</p>
                    <p className="text-[10px] text-indigo-600">
                      {b.status === 'overdue' ? `Overdue. Chase ${b.owner} immediately.` :
                       b.status === 'under-review' ? `Under review. Follow up with reviewer.` :
                       `Waiting on ${b.owner}. Send chase if overdue.`}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <Button size="sm" variant="outline" className="text-[10px] h-6 px-2 gap-1 border-indigo-200 text-indigo-600 hover:bg-indigo-50" onClick={() => handleChase(b.id)}><Send className="w-2.5 h-2.5" /> Chase</Button>
                      <Button size="sm" variant="outline" className="text-[10px] h-6 px-2 gap-1 border-emerald-200 text-emerald-600 hover:bg-emerald-50" onClick={() => handleResolve(b.id)}><CheckCircle2 className="w-2.5 h-2.5" /> Resolve</Button>
                    </div>
                  </div>
                )) : stage.state === 'cleared' ? (
                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                    <p className="text-xs text-emerald-700 font-medium flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Stage cleared</p>
                    <p className="text-[10px] text-emerald-600 mt-1">All conditions met. Trades can be released. Surveyor can be notified.</p>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">Waiting on upstream stages to clear.</p>
                )}
              </CardContent>
            </Card>

            {/* Quick links */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Related pages</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {[
                  { path: '/builder/trade-release', label: 'Trade Release', icon: Truck, desc: 'Release trades once ready' },
                  { path: '/builder/proof-review', label: 'Proof Review', icon: Eye, desc: 'Review uploaded evidence' },
                  { path: '/builder/job-blocker-copilot', label: 'Blocker Copilot', icon: Sparkles, desc: 'AI help with blockers' },
                  { path: '/builder/release-calendar', label: 'Release Calendar', icon: Calendar, desc: 'See upcoming events' },
                ].map(l => (
                  <Link key={l.path} to={l.path} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 transition-colors group">
                    <l.icon className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 group-hover:text-slate-900">{l.label}</p>
                      <p className="text-[9px] text-slate-400">{l.desc}</p>
                    </div>
                    <ChevronRight className="w-3 h-3 text-slate-300" />
                  </Link>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Blocker detail sheet */}
        <Sheet open={!!selectedBlocker} onOpenChange={() => setSelectedBlocker(null)}>
          <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="blocker-detail-sheet">
            {selectedBlocker && (
              <>
                <SheetHeader>
                  <SheetTitle className="font-heading text-lg">{selectedBlocker.title}</SheetTitle>
                  <SheetDescription>Blocking {stage.label} stage readiness</SheetDescription>
                </SheetHeader>
                <div className="mt-5 space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2"><Lock className="w-5 h-5 text-red-600" /><span className="text-sm font-semibold text-red-700">Blocking readiness</span></div>
                    <p className="text-xs text-red-600">{selectedBlocker.consequence || 'Until resolved, stage cannot clear, trades cannot be released.'}</p>
                  </div>
                  <div className="space-y-2">
                    {[
                      { label: "Type", value: selectedBlocker.type }, { label: "Owner", value: selectedBlocker.owner },
                      { label: "Due", value: selectedBlocker.dueDate }, { label: "Severity", value: selectedBlocker.severity },
                      { label: "Chased", value: `${selectedBlocker.chaseCount}x` },
                    ].map((r, i) => (
                      <div key={i} className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-xs text-slate-500">{r.label}</span>
                        <span className="text-xs font-medium text-slate-700">{r.value}</span>
                      </div>
                    ))}
                  </div>
                  {/* Timeline */}
                  {selectedBlocker.timeline?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-2">Timeline</p>
                      <div className="space-y-2">
                        {selectedBlocker.timeline.map((t, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs">
                            <span className="text-slate-400 shrink-0 w-20">{t.date}</span>
                            <span className="text-slate-700">{t.action}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <Separator />
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" onClick={() => handleResolve(selectedBlocker.id)}><Zap className="w-3.5 h-3.5" /> Resolve blocker</Button>
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => handleChase(selectedBlocker.id)}><Send className="w-3.5 h-3.5" /> Chase {selectedBlocker.owner}</Button>
                    <Link to="/builder/job-blocker-copilot"><Button size="sm" variant="ghost" className="text-indigo-600 gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Ask copilot</Button></Link>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  );
}
